import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { fetchUsers } from "@/lib/api";

interface UserState {
  userType:
    | "DONOR"
    | "CHARITY"
    | "DONOR & CHARITY"
    | "DONOR & DONORORG"
    | "GENERAL";
  canToggle: boolean;
  userSelection: "DONOR" | "CHARITY" | "DONORORG" | null; // ← added DONORORG
}

const initialState: UserState = {
  userType: "GENERAL",
  canToggle: false,
  userSelection: null,
};

const loadUserSelectionFromLocalStorage = ():
  | "DONOR"
  | "CHARITY"
  | "DONORORG"
  | null => {
  try {
    const storedUserSelection = localStorage.getItem("userSelectedProfile");
    if (!storedUserSelection || storedUserSelection === "null") {
      return null;
    }

    // Validate that the stored value is one of the expected types
    if (
      storedUserSelection === "DONOR" ||
      storedUserSelection === "CHARITY" ||
      storedUserSelection === "DONORORG"
    ) {
      return storedUserSelection as "DONOR" | "CHARITY" | "DONORORG";
    }

    return null;
  } catch (error) {
    console.warn("Failed to load user selection from localStorage:", error);
    return null;
  }
};

const saveUserSelectionToLocalStorage = (
  selection: "DONOR" | "CHARITY" | "DONORORG" | null,
) => {
  try {
    if (selection === null) {
      localStorage.removeItem("userSelectedProfile");
    } else {
      localStorage.setItem("userSelectedProfile", selection);
    }
  } catch (error) {
    console.warn("Failed to save user selection to localStorage:", error);
  }
};

const initialStateWithLocalStorage = {
  ...initialState,
  userSelection: loadUserSelectionFromLocalStorage(),
};

export const fetchUserType = createAsyncThunk(
  "user/fetchUserType",
  async (
    { userId, token }: { userId: string | null; token: string | null },
    thunkAPI,
  ) => {
    try {
      const userData = await fetchUsers(userId, token);
      return userData;
    } catch (error) {
      return thunkAPI.rejectWithValue("Failed to fetch user type");
    }
  },
);

const userSlice = createSlice({
  name: "user",
  initialState: initialStateWithLocalStorage,
  reducers: {
    setUserSelection: (
      state,
      action: PayloadAction<"DONOR" | "CHARITY" | "DONORORG" | null>,
    ) => {
      state.userSelection = action.payload;
      saveUserSelectionToLocalStorage(action.payload);
    },
    clearUserSelection: (state) => {
      state.userSelection = null;
      saveUserSelectionToLocalStorage(null);
    },
    syncWithLocalStorage: (state) => {
      const storedSelection = loadUserSelectionFromLocalStorage();
      state.userSelection = storedSelection;
    },
  },
  extraReducers: (builder) => {
    builder.addCase("auth/logout", (state) => {
      state.userType = "GENERAL";
      state.canToggle = false;
      state.userSelection = null;
      saveUserSelectionToLocalStorage(null);
    });

    builder.addCase(fetchUserType.fulfilled, (state, action) => {
      const userTypeFromDB = action.payload?.usertype || "GENERAL";
      state.userType = userTypeFromDB;

      // Enable toggling for combination user types
      state.canToggle =
        userTypeFromDB === "DONOR & CHARITY" ||
        userTypeFromDB === "DONOR & DONORORG";

      let newSelection: "DONOR" | "CHARITY" | "DONORORG" | null = null;

      if (userTypeFromDB === "DONOR") {
        newSelection = "DONOR";
      } else if (userTypeFromDB === "CHARITY") {
        newSelection = "CHARITY";
      } else if (userTypeFromDB === "DONOR & CHARITY") {
        const storedSelection = loadUserSelectionFromLocalStorage();
        newSelection =
          storedSelection === "DONOR" || storedSelection === "CHARITY"
            ? storedSelection
            : "CHARITY"; // default to CHARITY
      } else if (userTypeFromDB === "DONOR & DONORORG") {
        const storedSelection = loadUserSelectionFromLocalStorage();
        newSelection =
          storedSelection === "DONOR" || storedSelection === "DONORORG"
            ? storedSelection
            : "DONORORG"; // default to DONORORG
      } else {
        newSelection = null;
      }

      if (state.userSelection !== newSelection) {
        state.userSelection = newSelection;
        saveUserSelectionToLocalStorage(newSelection);
      }
    });

    builder.addCase(fetchUserType.rejected, (state: any) => {
      console.warn("Failed to fetch user type", state);
    });
  },
});

export const { setUserSelection, clearUserSelection, syncWithLocalStorage } =
  userSlice.actions;

export const selectedUserType = (state: RootState) => state.user.userSelection;
export const canToggle = (state: RootState) => state.user.canToggle;
export const userTypeDB = (state: RootState) => state.user.userType;

export default userSlice.reducer;
